<template>
    <div class="max-w-8xl mx-auto w-full space-y-4">
        <!-- Page Header -->
        <div class="flex flex-wrap items-center justify-between gap-3">
            <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                Assignments Management
            </h1>
            <button @click="openModal" class="btn btn-primary">
                <Plus class="w-4 h-4 mr-1" />
                Add Assignment
            </button>
        </div>

        <!-- Search and Filters -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div class="space-y-3">
                <!-- Search -->
                <input
                    v-model="searchQuery"
                    @input="debouncedSearch"
                    type="text"
                    placeholder="Search by title..."
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />

                <div class="flex flex-wrap gap-3">
                    <!-- Class Subject Filter -->
                    <SearchableSelect
                        v-model="filters.class_subject_id"
                        :options="classSubjectOptions"
                        placeholder="All Class Subjects"
                        :option-label="getClassSubjectLabel"
                        option-value="id"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchAssignments"
                    />

                    <!-- Teacher Filter -->
                    <SearchableSelect
                        v-model="filters.teacher_id"
                        :options="teacherOptions"
                        placeholder="All Teachers"
                        :option-label="getTeacherName"
                        option-value="id"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchAssignments"
                    />

                    <!-- Status Filter -->
                    <SearchableSelect
                        v-model="filters.status"
                        :options="statusOptions"
                        placeholder="All Statuses"
                        option-label="label"
                        option-value="value"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchAssignments"
                    />

                    <!-- Class Filter -->
                    <SearchableSelect
                        v-model="filters.class_id"
                        :options="classOptions"
                        placeholder="All Classes"
                        :option-label="getClassName"
                        option-value="id"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchAssignments"
                    />

                    <!-- Subject Filter -->
                    <SearchableSelect
                        v-model="filters.subject_id"
                        :options="subjectOptions"
                        placeholder="All Subjects"
                        :option-label="getSubjectName"
                        option-value="id"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchAssignments"
                    />

                    <button
                        @click="resetFilters()"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    >
                        <RotateCcw class="w-4 h-4" />
                        Reset Filters
                    </button>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div
            v-if="loading"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <SkeletonLoader variant="table" :rows="6" :cols="7" />
        </div>

        <!-- Error State -->
        <div
            v-if="error"
            class="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4"
        >
            <div class="flex">
                <div class="flex-shrink-0">
                    <div class="h-5 w-5 text-danger-400">⚠️</div>
                </div>
                <div class="ml-3">
                    <h3
                        class="text-sm font-medium text-danger-800 dark:text-danger-200"
                    >
                        Error
                    </h3>
                    <p
                        class="mt-1 text-sm text-danger-700 dark:text-danger-300"
                    >
                        {{ error }}
                    </p>
                    <button
                        @click="clearError"
                        class="mt-2 text-sm text-danger-600 dark:text-danger-400 hover:text-danger-500"
                    >
                        Dismiss
                    </button>
                </div>
            </div>
        </div>

        <!-- Data Table -->
        <div
            v-if="!loading && !error"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <div class="overflow-x-auto">
                <table
                    class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                    style="width: 100%"
                >
                    <thead class="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Title
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Class Subject
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Deadline
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Max Score
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Submissions
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Status
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody
                        class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                    >
                        <tr
                            v-for="assignment in assignments"
                            :key="assignment.id"
                            class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            <td class="px-6 py-4 whitespace-nowrap">
                                <div
                                    class="text-sm font-medium text-gray-900 dark:text-white"
                                >
                                    {{ assignment.title }}
                                </div>
                                <div
                                    v-if="assignment.description"
                                    class="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs"
                                    :title="assignment.description"
                                >
                                    {{ assignment.description }}
                                </div>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{
                                    assignment.class_subject?.class
                                        ?.grade_level || "-"
                                }}
                                -
                                {{
                                    assignment.class_subject?.class?.name || "-"
                                }}
                                -
                                {{
                                    assignment.class_subject?.subject?.name ||
                                    "-"
                                }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                <span
                                    :class="{
                                        'text-danger-600 dark:text-danger-400':
                                            assignment.is_overdue,
                                        'text-warning-600 dark:text-warning-400':
                                            !assignment.is_overdue &&
                                            assignment.days_until_deadline !==
                                                null &&
                                            assignment.days_until_deadline !==
                                                undefined &&
                                            assignment.days_until_deadline <= 3,
                                    }"
                                >
                                    {{ formatDate(assignment.deadline) }}
                                </span>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ assignment.max_score }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ assignment.submissions_count || 0 }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span
                                    v-if="assignment.is_published"
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200"
                                >
                                    Published
                                </span>
                                <span
                                    v-else
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-danger-100 text-danger-800 dark:bg-danger-900 dark:text-danger-200"
                                >
                                    Draft
                                </span>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                            >
                                <button
                                    @click="viewAssignment(assignment)"
                                    title="View Assignment"
                                    class="inline-flex items-center justify-center p-2 text-success-600 dark:text-success-400 hover:text-success-900 dark:hover:text-success-300 rounded-md hover:bg-success-50 dark:hover:bg-success-900/20 transition-colors mr-2"
                                >
                                    <Eye class="w-4 h-4" />
                                </button>
                                <button
                                    @click="editAssignment(assignment)"
                                    title="Edit Assignment"
                                    class="inline-flex items-center justify-center p-2 text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 rounded-md hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors mr-2"
                                >
                                    <Pencil class="w-4 h-4" />
                                </button>
                                <button
                                    @click="viewSubmissions(assignment)"
                                    title="View Submissions"
                                    class="inline-flex items-center justify-center p-2 text-purple-600 dark:text-purple-400 hover:text-purple-900 dark:hover:text-purple-300 rounded-md hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors mr-2"
                                >
                                    <ClipboardList class="w-4 h-4" />
                                </button>
                                <button
                                    @click="confirmDelete(assignment)"
                                    title="Delete Assignment"
                                    class="inline-flex items-center justify-center p-2 text-danger-600 dark:text-danger-400 hover:text-danger-900 dark:hover:text-danger-300 rounded-md hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                                >
                                    <Trash2 class="w-4 h-4" />
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination -->
            <BasePagination
                :pagination="meta"
                :per-page="filters.per_page"
                :per-page-options="[10, 15, 25, 50, 100]"
                @page-change="goToPage"
                @per-page-change="handlePerPageChange"
            />
        </div>

        <DeleteConfirmationModal
            :show="showDeleteModal"
            title="Delete Assignment"
            :message="
                assignmentToDelete
                    ? `Are you sure you want to delete '${assignmentToDelete.title}'? This action cannot be undone.`
                    : ''
            "
            @confirm="deleteAssignment"
            @close="closeDeleteModal"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import { debounce } from "lodash-es";
import SearchableSelect from "@/components/SearchableSelect.vue";
import BasePagination from "@/components/BasePagination.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal.vue";
import {
    RotateCcw,
    Eye,
    Pencil,
    ClipboardList,
    Trash2,
    Plus,
} from "@lucide/vue";
import assignmentApi, { Assignment, AssignmentFilters } from "@/api/assignment";
const router = useRouter();
const assignments = ref<Assignment[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const meta = ref<any>(null);

const searchQuery = ref("");
const filters = ref<AssignmentFilters>({
    status: undefined,
    class_id: undefined,
    subject_id: undefined,
    teacher_id: undefined,
    class_subject_id: undefined,
    per_page: 15,
});

const classSubjectOptions = ref<any[]>([]);
const teacherOptions = ref<any[]>([]);
const classOptions = ref<any[]>([]);
const subjectOptions = ref<any[]>([]);

const statusOptions = [
    { label: "Published", value: "published" },
    { label: "Draft", value: "draft" },
];

const hasActiveFilters = computed(() => {
    const { status, class_id, subject_id, teacher_id, class_subject_id } =
        filters.value;
    return (
        searchQuery.value.trim() !== "" ||
        status !== undefined ||
        class_id !== undefined ||
        subject_id !== undefined ||
        teacher_id !== undefined ||
        class_subject_id !== undefined
    );
});

const debouncedSearch = debounce(() => {
    fetchAssignments();
}, 500);

async function fetchAssignments() {
    loading.value = true;
    error.value = null;

    try {
        const response = await assignmentApi.getAll({
            ...filters.value,
            search: searchQuery.value || undefined,
        });

        assignments.value = response.data.items;
        meta.value = response.data.meta;
    } catch (err: any) {
        error.value =
            err.response?.data?.message || "Failed to fetch assignments";
    } finally {
        loading.value = false;
    }
}

function resetFilters() {
    filters.value = {
        status: undefined,
        class_id: undefined,
        subject_id: undefined,
        teacher_id: undefined,
        class_subject_id: undefined,
        per_page: 15,
    };
    searchQuery.value = "";
    fetchAssignments();
}

function clearError() {
    error.value = null;
}

function formatDate(dateString: string) {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function getClassSubjectLabel(option: any) {
    return `${option.class?.grade_level} - ${option.class?.name} - ${option.subject?.name}`;
}

function getTeacherName(option: any) {
    return option.user?.name || "-";
}

function getClassName(option: any) {
    return `${option.grade_level} - ${option.name}`;
}

function getSubjectName(option: any) {
    return `${option.code} - ${option.name}`;
}

function openModal() {
    router.push("/assignments/create");
}

function viewAssignment(assignment: Assignment) {
    router.push(`/assignments/${assignment.id}`);
}

function editAssignment(assignment: Assignment) {
    router.push(`/assignments/${assignment.id}/edit`);
}

function viewSubmissions(assignment: Assignment) {
    router.push(`/assignments/${assignment.id}/submissions`);
}

const showDeleteModal = ref(false);
const assignmentToDelete = ref<Assignment | null>(null);

function confirmDelete(assignment: Assignment) {
    assignmentToDelete.value = assignment;
    showDeleteModal.value = true;
}

function closeDeleteModal() {
    showDeleteModal.value = false;
    assignmentToDelete.value = null;
}

async function deleteAssignment() {
    if (!assignmentToDelete.value) return;

    try {
        await assignmentApi.delete(assignmentToDelete.value.id);
        await fetchAssignments();
    } catch (err: any) {
        error.value =
            err.response?.data?.message || "Failed to delete assignment";
    } finally {
        closeDeleteModal();
    }
}

function goToPage(page: number) {
    if (meta.value) {
        filters.value.page = page;
        fetchAssignments();
    }
}

function handlePerPageChange(value: number) {
    filters.value.per_page = value;
    filters.value.page = 1;
    fetchAssignments();
}

onMounted(() => {
    fetchAssignments();
});
</script>
